import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { formatTimeAgo } from 'helpers/Datetime.helper';
import { getCurrentLanguageCode } from "helpers/String.helper";
import { INotice } from 'api/interfaces/bbs.interface';
import { LoadingComponent } from 'commons/loading';
import NoticeListSkeleton from './NoticeListSkeleton';

interface NoticeListProps {
    noticeList: INotice[];
    isLoading: boolean;
    isLoadingMore: boolean;
    hasMore: boolean;
}

const NoticeListComponent: React.FC<NoticeListProps> = ({ noticeList, isLoading, isLoadingMore, hasMore }) => {
    const navigate = useNavigate();
    const currentLangCode = getCurrentLanguageCode();

    return (
        <main className="bg-gray-50 min-h-screen py-6">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center gap-4 mb-6">
                    <button onClick={() => navigate('/koach/my')} className="text-gray-600 hover:text-gray-900">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>
                    <h1 className="text-2xl font-bold text-gray-900">
                        공지사항
                    </h1>
                </div>
                <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                    <div className="p-4">
                        {isLoading ? (
                            <NoticeListSkeleton />
                        ) : noticeList.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-6 text-gray-500">
                                <svg className="w-12 h-12 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                <p className="text-center">
                                    공지사항이 없습니다.
                                </p>
                            </div>
                        ) : (
                            <div>
                                <div className="space-y-3">
                                    {noticeList.map((notice) => {
                                        // 현재 언어에 맞는 공지사항 내용 찾기
                                        const currentContent = notice.contents?.find(
                                            content => content.languageCode === currentLangCode
                                        ) || notice.contents?.find(
                                            content => content.languageCode === 'en'
                                        ) || notice.contents?.[0];

                                        if (!currentContent) return null;

                                        return (
                                            <Link
                                                key={notice.id}
                                                to={`/koach/my/notice/${notice.id}`}
                                                className="block bg-white border border-gray-200 rounded-lg p-3 hover:shadow-sm transition-shadow cursor-pointer"
                                            >
                                                <div className="flex items-start justify-between">
                                                    <div className="flex-1">
                                                        <div className="flex items-center gap-2 mb-1">
                                                            {notice.isImportant && (
                                                                <span className="inline-flex items-center text-red-800">
                                                                    <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01M12 17a5 5 0 100-10 5 5 0 000 10zm0 0v.01" />
                                                                    </svg>
                                                                </span>
                                                            )}
                                                            <div className="relative">
                                                                {notice.isNew && (
                                                                    <span className="absolute -top-1 -left-2 w-2 h-2 rounded-full bg-[#CF3688] z-10 border-2 border-white"></span>
                                                                )}
                                                                <h3 className="text-base font-semibold text-gray-900 line-clamp-2 relative">
                                                                    {currentContent.title}
                                                                </h3>
                                                            </div>
                                                        </div>
                                                        {currentContent.content && (
                                                            <p className="text-gray-600 text-sm line-clamp-2 mb-2">
                                                                {currentContent.content.replace(/<[^>]*>/g, '')}
                                                            </p>
                                                        )}
                                                        <div className="flex items-center gap-4 text-xs text-gray-500">
                                                            <span>{formatTimeAgo(notice.createdAt)}</span>
                                                        </div>
                                                    </div>
                                                    <div className="ml-4">
                                                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                                                        </svg>
                                                    </div>
                                                </div>
                                            </Link>
                                        );
                                    })}
                                </div>

                                {/* 추가 로딩 상태 */}
                                {isLoadingMore && (
                                    <div className="flex justify-center items-center py-8">
                                        <div className="flex items-center space-x-2">
                                            <svg className="w-5 h-5 animate-spin text-[#CF3688]" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            <span className="text-sm text-gray-500">더 많은 공지사항을 불러오는 중...</span>
                                        </div>
                                    </div>
                                )}

                                {/* 더 이상 로드할 데이터가 없을 때 */}
                                {!isLoadingMore && !hasMore && noticeList.length > 0 && (
                                    <div className="flex justify-center items-center py-8">
                                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                                            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </main>
    );
};

export default NoticeListComponent;
